<script lang="ts">
  // Indexed market-growth area chart (2024 = 100) compounding at ~36.7% / yr.
  let {
    cagr,
    unit,
    years,
  }: { cagr: string; unit: string; years: string[] } = $props();

  const W = 360;
  const H = 210;
  const padL = 14;
  const padR = 14;
  const top = 26;
  const base = 168;

  const geom = $derived.by(() => {
    const n = years.length;
    const values = Array.from({ length: n }, (_, i) => 100 * Math.pow(1.367, i));
    const max = values[n - 1];
    const x = (i: number) => padL + (i * (W - padL - padR)) / (n - 1);
    const y = (v: number) => base - (v / max) * (base - top);
    const pts = values.map((v, i) => [x(i), y(v)] as [number, number]);
    const line = pts
      .map((p, i) => `${i ? "L" : "M"} ${p[0].toFixed(1)} ${p[1].toFixed(1)}`)
      .join(" ");
    const area = `${line} L ${x(n - 1).toFixed(1)} ${base} L ${padL} ${base} Z`;
    const grid = [0.25, 0.5, 0.75, 1].map((f) => base - f * (base - top));
    return { n, pts, line, area, grid, cagrX: x(1), cagrY: y(values[1]) };
  });
</script>

<svg class="chart" viewBox="0 0 {W} {H}" role="img" aria-label={unit}>
  <defs>
    <linearGradient id="g-fill" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stop-color="rgba(95,179,100,0.4)" />
      <stop offset="100%" stop-color="rgba(95,179,100,0.02)" />
    </linearGradient>
  </defs>

  <!-- gridlines -->
  {#each geom.grid as gy}
    <line x1={padL} y1={gy} x2={W - padR} y2={gy} stroke="var(--line)" stroke-width="1" />
  {/each}

  <!-- area + line -->
  <path d={geom.area} fill="url(#g-fill)" />
  <path d={geom.line} fill="none" stroke="var(--green)" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" />

  <!-- points -->
  {#each geom.pts as p, i}
    <circle cx={p[0]} cy={p[1]} r={i === geom.n - 1 ? 5 : 3} fill={i === geom.n - 1 ? "var(--green)" : "var(--paper-card)"} stroke="var(--green)" stroke-width="2" />
  {/each}

  <!-- CAGR badge -->
  <g transform="translate({geom.cagrX}, {geom.cagrY - 4})">
    <rect x="-2" y="-30" width="92" height="22" rx="11" fill="var(--green)" />
    <text x="44" y="-15" text-anchor="middle" class="cagr">{cagr}</text>
  </g>

  <!-- year labels -->
  {#each years as yr, i}
    <text x={geom.pts[i][0]} y={base + 16} text-anchor="middle" class="yr">{yr}</text>
  {/each}
  <text x={padL} y={top - 12} class="unit">{unit}</text>
</svg>

<style>
  .chart {
    width: 100%;
    height: auto;
    overflow: visible;
  }
  .cagr {
    font-family: var(--font-mono);
    font-size: 11px;
    font-weight: 700;
    fill: #fff;
  }
  .yr {
    font-family: var(--font-mono);
    font-size: 9px;
    fill: var(--muted);
  }
  .unit {
    font-family: var(--font-mono);
    font-size: 8.5px;
    letter-spacing: 0.04em;
    fill: var(--muted);
  }
</style>
